import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format, startOfYear } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getYearTimecards } from '../../../services/timecardService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';

// Utility functions
const getLocalYearStart = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return startOfYear(localDate);
};

// Sticky column styles
const stickyColumnStyles = {
    username: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        backgroundColor: 'background.paper',
        minWidth: '150px',
        borderRight: '1px solid rgba(224, 224, 224, 1)',
        padding: '16px',
        '@media (max-width:900px)': {
            minWidth: '100px',
            padding: '8px',
            fontSize: '0.75rem',
        }
    },
    project: {
        position: 'sticky',
        left: '150px',
        zIndex: 3,
        backgroundColor: 'background.paper',
        minWidth: '200px',
        borderRight: '2px solid rgba(224, 224, 224, 1)', // Thicker border as last sticky column
        padding: '16px',
        '@media (max-width:900px)': {
            left: '100px', // Adjust for smaller username column
            minWidth: '120px',
            padding: '8px',
            fontSize: '0.75rem',
        }
    }
};

const YearlyViewAll = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const { user, loading: authLoading } = useAuth();

    // Memoize currentYearStart to prevent unnecessary recalculations
    const currentYearStart = useMemo(() => getLocalYearStart(currentDate), [currentDate]);

    // Fetch timecards when date changes
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchTimecardData = async () => {
            try {
                setDataLoading(true);
                const year = currentYearStart.getFullYear();
                
                console.log('Fetching timecards for all users:', {
                    year,
                    userRole: user?.role
                });
                
                const data = await getYearTimecards(year, null, 'all');
                if (mounted) {
                    // Filter timecards based on user role
                    let filteredTimecards = [...data];
                    
                    // If user is a team lead, filter to only show their team members
                    if (user.role === 'team_lead') {
                        console.log('User is a team lead, filtering timecards');
                        
                        // Get team member IDs
                        let teamMemberIds = [];
                        
                        // Check if user has teamMembers directly
                        if (user.teamMembers && user.teamMembers.length > 0) {
                            console.log('Using teamMembers array directly:', user.teamMembers);
                            teamMemberIds = user.teamMembers.map(id => String(id));
                            // Add the team lead to the list
                            if (!teamMemberIds.includes(String(user.id))) {
                                teamMemberIds.push(String(user.id));
                            }
                        } else if (user.teams && user.teams.length > 0) {
                            // Get only the teams where the user is the team lead
                            const leadTeams = user.teams.filter(team => 
                                String(team.teamLead) === String(user.id)
                            );
                            
                            if (leadTeams.length > 0) {
                                // Get all members from these teams
                                const teamMembers = leadTeams.flatMap(team => team.members || []);
                                
                                // Add the team lead to the list of members if not already included
                                if (!teamMembers.includes(user.id) && !teamMembers.some(id => String(id) === String(user.id))) {
                                    teamMembers.push(user.id);
                                }
                                
                                // Make sure we don't have duplicates
                                teamMemberIds = [...new Set(teamMembers.map(id => String(id)))];
                            } else {
                                // If user is not a team lead for any team, they can only see themselves
                                teamMemberIds = [String(user.id)];
                            }
                        } else {
                            // If user has no teams or teamMembers, they can only see themselves
                            teamMemberIds = [String(user.id)];
                        }
                        
                        console.log('Team member IDs:', teamMemberIds);
                        
                        // Filter timecards to only show those for team members
                        filteredTimecards = filteredTimecards.filter(timecard => 
                            timecard.user && teamMemberIds.includes(String(timecard.user._id))
                        );
                        
                        console.log('Filtered timecards count:', filteredTimecards.length);
                    }
                    
                    // Group timecards by username AND project name, then aggregate hours
                    const userProjectGroups = filteredTimecards.reduce((groups, timecard) => {
                        const username = timecard.user?.username || 'Unknown User';
                        const projectName = timecard.project?.name || 'Unknown Project';
                        const groupKey = `${username}|${projectName}`; // Composite key for username + project

                        if (!groups[groupKey]) {
                            groups[groupKey] = {
                                _id: groupKey,
                                user: { username, _id: timecard.user?._id },
                                project: { name: projectName },
                                months: Array(12).fill(null).map(() => ({ hours: 0 }))
                            };
                        }
                        
                        // Add hours from this timecard to the user-project group
                        timecard.months?.forEach((month, index) => {
                            if (month && month.hours) {
                                groups[groupKey].months[index].hours += parseFloat(month.hours);
                            }
                        });
                        
                        return groups;
                    }, {});

                    // Convert groups object to array and sort by username then project name
                    const aggregatedData = Object.values(userProjectGroups)
                        .sort((a, b) => {
                            // First sort by username
                            const usernameCompare = (a.user?.username || '').localeCompare(b.user?.username || '');
                            if (usernameCompare !== 0) return usernameCompare;
                            // Then sort by project name
                            return (a.project?.name || '').localeCompare(b.project?.name || '');
                        });

                    console.log('Timecards received and aggregated:', {
                        originalCount: data.length,
                        filteredCount: filteredTimecards.length,
                        aggregatedCount: aggregatedData.length,
                        year
                    });
                    
                    setTimecards(aggregatedData);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching timecards:', err);
                if (mounted) {
                    setError(err.message);
                    setTimecards([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        // Only fetch if we have a user and they are admin/team lead/accountant
        if (user && (user.role === 'admin' || user.role === 'team_lead' || user.role === 'accountant')) {
            fetchTimecardData();
        }

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [currentYearStart, user]);

    const handlePreviousYear = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setFullYear(newDate.getFullYear() - 1);
            return newDate;
        });
    };

    const handleNextYear = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setFullYear(newDate.getFullYear() + 1);
            return newDate;
        });
    };


    // Memoize year info calculation
    const yearInfo = useMemo(() => {
        const months = Array.from({ length: 12 }, (_, i) => {
            const date = new Date(currentYearStart);
            date.setMonth(i);
            return {
                date: date.toISOString()
            };
        });
        
        return { months };
    }, [currentYearStart]);

    const { months } = yearInfo;

    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data or insufficient permissions
    if (!user || (user.role !== 'admin' && user.role !== 'team_lead' && user.role !== 'accountant')) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">
                    {!user ? 'Authentication required' : 'Insufficient permissions'}
                </Alert>
            </Box>
        );
    }

    console.log('Rendering year for all users:', {
        year: currentYearStart.getFullYear(),
        userRole: user.role
    });

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    Yearly View - All Users
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            marginLeft: '-8px', // Move the entire year navigation widget to the left
                        }
                    }}>
                        <IconButton 
                            onClick={handlePreviousYear}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <Typography sx={{
                            '@media (max-width:600px)': {
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }
                        }}>
                            {format(currentYearStart, 'yyyy', { locale: enUS })}
                        </Typography>
                        <IconButton 
                            onClick={handleNextYear}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    <ExportButton
                        viewType="yearly"
                        timecards={timecards}
                        isAllUsers={true}
                        date={currentYearStart}
                        mobileIconOnly={true}
                    />
                </Box>
            </Paper>

            {/* Timecard Table */}
            <TableContainer 
                component={Paper}
                sx={{
                    overflow: 'auto',
                    '& .MuiTable-root': {
                        minWidth: months.length * 120 + 350, // Account for sticky columns (username + project) + months
                        borderCollapse: 'separate',
                        borderSpacing: 0
                    }
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={stickyColumnStyles.username}>
                                Username
                            </TableCell>
                            <TableCell sx={stickyColumnStyles.project}>
                                Project
                            </TableCell>
                            {months.map((month, index) => {
                                const date = new Date(month.date);
                                return (
                                    <TableCell 
                                        key={index} 
                                        align="center"
                                        sx={{
                                            width: `${100 / months.length}%`,
                                            minWidth: '120px',
                                            padding: '16px',
                                            whiteSpace: 'nowrap',
                                            boxSizing: 'border-box',
                                            '@media (max-width:900px)': {
                                                minWidth: '80px',
                                                padding: '8px',
                                                fontSize: '0.7rem',
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'block',
                                            '@media (max-width:900px)': {
                                                display: 'none',
                                            }
                                        }}>
                                            {format(date, 'MMMM yyyy', { locale: enUS })}
                                        </Box>
                                        <Box sx={{
                                            display: 'none',
                                            '@media (max-width:900px)': {
                                                display: 'block',
                                            }
                                        }}>
                                            {format(date, 'MMM', { locale: enUS })}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                            <TableCell 
                                align="center"
                                sx={{
                                    minWidth: '120px',
                                    padding: '16px',
                                    '@media (max-width:900px)': {
                                        minWidth: '80px',
                                        padding: '8px',
                                        fontSize: '0.75rem',
                                    }
                                }}
                            >
                                Total
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timecards.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={months.length + 3} align="center">
                                    No timecard data found for this year
                                </TableCell>
                            </TableRow>
                        ) : (
                            timecards.map((timecard) => (
                                <TableRow key={timecard._id}>
                                    <TableCell sx={stickyColumnStyles.username}>
                                        {timecard.user?.username || 'Unknown User'}
                                    </TableCell>
                                    <TableCell sx={stickyColumnStyles.project}>
                                        {timecard.project?.name || 'Unknown Project'}
                                    </TableCell>
                                    {months.map((month, index) => {
                                        const monthData = timecard.months?.[index];
                                        return (
                                            <TableCell 
                                                key={index} 
                                                align="center"
                                                sx={{
                                                    minWidth: '120px',
                                                    padding: '16px',
                                                    '@media (max-width:900px)': {
                                                        minWidth: '80px',
                                                        padding: '8px',
                                                        fontSize: '0.75rem',
                                                    }
                                                }}
                                            >
                                                {monthData ? parseFloat(monthData.hours).toFixed(2) : '0.00'}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell 
                                        align="center"
                                        sx={{
                                            minWidth: '120px',
                                            padding: '16px',
                                            '@media (max-width:900px)': {
                                                minWidth: '80px',
                                                padding: '8px',
                                                fontSize: '0.75rem',
                                            }
                                        }}
                                    >
                                        {(timecard.months || [])
                                            .reduce((sum, month) => sum + (month ? parseFloat(month.hours) : 0), 0)
                                            .toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default YearlyViewAll;
